<script lang="ts">
    import { service } from "@frontend/services";
    import { GetCachedToken, me } from "@frontend/user";
    import { groups } from "@frontend/grouplist";
    import { RefreshGroupLimits } from "@frontend/grouplimitlist";
    import { translate } from "../Translator";
    import type { AuthenticatedRequestOfAddGroupLimitDto, AddGroupLimitDto, Money, User } from "@frontend/walletapi";
    import { usersByRole, RefreshUsers } from "@frontend/userlist";
    import { onMount } from "svelte";
    import { get } from "svelte/store";


    export let dismiss: () => void;

    let groupId: number | undefined = undefined;
    let userId: number | undefined = undefined;
    let initialBalance: number = 0;
    let balance: number = 0;
    let isActive: boolean = true;
    let errorMessage = '';
    let successMessage = '';
    let isSubmitting = false;
    
    // Lista de todos los usuarios
    let allUsers: User[] = [];
    
    // Función para obtener todos los usuarios de todos los roles
    function getAllUsers(): User[] {
        const allUsersList: User[] = [];
        Object.values(usersByRole).forEach(userStore => {
            const users = get(userStore);
            allUsersList.push(...users);
        });
        return allUsersList.sort((a, b) => (a.FirstName || '').localeCompare(b.FirstName || ''));
    }
    
    // Función para obtener el nombre completo del usuario
    function getUserDisplayName(user: User): string {
        const firstName = user.FirstName || '';
        const lastName = user.LastName || '';
        const email = user.Email || '';
        return `${firstName} ${lastName}`.trim() || email || `ID: ${user.Id}`;
    }

    // Función para crear objeto Money
    function createMoney(amount: number): Money {
        return {
            InCents: Math.round(amount * 100)
        };
    }

    async function submitForm() {
        if (!groupId) {
            errorMessage = 'Please select a group.';
            return;
        }

        if (initialBalance < 0) {
            errorMessage = 'The opening balance cannot be negative.';
            return;
        }

        if (balance < 0) {
            errorMessage = 'The balance cannot be negative.';
            return;
        }

        isSubmitting = true;
        errorMessage = '';

        try {
            const requestPayload: AuthenticatedRequestOfAddGroupLimitDto = {
                Token: GetCachedToken(),
                Data: {
                    GroupId: groupId,
                    UserId: userId || undefined,
                    InitialBalance: createMoney(initialBalance),
                    Balance: createMoney(balance),
                    DateTransaction: new Date(),
                    Active: isActive
                }
            };

            const result = await service.walletapi.addGroupLimit(requestPayload);

            if (result.Success) {
                successMessage = result.Message || 'Group limit successfully added.';
                setTimeout(() => {
                    successMessage = '';
                    dismiss();
                }, 2000);
                await RefreshGroupLimits();
            } else {
                errorMessage = result.ErrorMessage || 'Error adding group limit.';
            }
        } catch (error) {
            errorMessage = 'Connection error. Please try again.';
        } finally {
            isSubmitting = false;
        }
    }

    function handleCancel() {
        dismiss();
    }
    
    // Cargar usuarios al montar el componente
    onMount(async () => {
        await RefreshUsers();
        allUsers = getAllUsers();
        
        // Establecer el usuario logueado por defecto si existe
        if ($me && $me.Id) {
            userId = $me.Id;
        }
    });
    
    // Actualizar lista de usuarios cuando cambien los stores
    $: {
        allUsers = getAllUsers();
    }
</script>

<style>
    .form-container {
        padding: 1em;
    }
    .field-group {
        margin-bottom: 1em;
    }
 
</style>

<div class="form-container">
    <h2 class="ui header">{$translate('Add Group Limit')}</h2>

    {#if errorMessage}
        <div class="ui negative message">
            <div class="header">{$translate('Error')}</div>
            <p>{errorMessage}</p>
        </div>
    {/if}

    {#if successMessage}
        <div class="ui positive message">
            <div class="header">{$translate('Success')}</div>
            <p>{successMessage}</p>
        </div>
    {/if}

    <form class="ui form" on:submit|preventDefault={submitForm}>
        <div class="field-group">
            <label for="groupId">{$translate('Grupo *')}</label>
            <select id="groupId" class="ui dropdown" bind:value={groupId} required>
                <option value={undefined}>{$translate('Select group...')}</option>
                {#each $groups as group}
                    <option value={group.Id}>{group.Name} (ID: {group.Id})</option>
                {/each}
            </select>
        </div>

        <div class="field-group">
            <label for="userId">{$translate('Usuario (Opcional)')}</label>
            <select id="userId" class="ui dropdown" bind:value={userId}>
                <option value={undefined}>{$translate('Select user (optional)...')}</option>
                {#each allUsers as user}
                    <option value={user.Id} selected={user.Id === $me?.Id}>
                        {getUserDisplayName(user)} - {user.Role} (ID: {user.Id})
                        {#if user.Id === $me?.Id} ← Tú{/if}
                    </option>
                {/each}
            </select>
            {#if $me}
                <small class="ui grey text">{$translate('Default selection:')} {$me.FirstName} {$me.LastName} ({$me.Email})</small>
            {/if}
        </div>

        <div class="field-group">
            <label for="initialBalance">{$translate('Initial Balance*')}</label>
            <div class="ui labeled input">
                <div class="ui label">$</div>
                <input 
                    id="initialBalance" 
                    type="number" 
                    placeholder="0.00"
                    bind:value={initialBalance}
                    step="0.01"
                    min="0"
                    required
                />
            </div>
        </div>

        <div class="field-group">
            <label for="balance">{$translate('Current Balance *')}</label>
            <div class="ui labeled input">
                <div class="ui label">$</div>
                <input 
                    id="balance" 
                    type="number" 
                    placeholder="0.00"
                    bind:value={balance}
                    step="0.01"
                    min="0"
                    required
                />
            </div>
        </div>

        <div class="field-group">
            <label for="isActive">{$translate('Estado')}</label>
            <div class="ui toggle checkbox">
                <input 
                    id="isActive" 
                    type="checkbox" 
                    bind:checked={isActive}
                />
                <label for="isActive">
                    {#if isActive}
                        <span class="ui green text">{$translate('Active')}</span>
                    {:else}
                        <span class="ui red text">{$translate('Inactive')}</span>
                    {/if}
                </label>
            </div>
            <small class="ui grey text">{$translate('Determines whether the group limit is active or inactive.')}</small>
        </div>

        <div class="ui divider"></div>

        <div class="ui buttons">
            <button type="button" class="ui button" on:click={handleCancel}>
                Cancel
            </button>
            <div class="or"></div>
            <button type="submit" class="ui positive button" disabled={isSubmitting}>
                {#if isSubmitting}
                    <i class="spinner loading icon"></i>
                    {$translate('Adding...')}
                {:else}
                    <i class="plus icon"></i>
                   {$translate('Add Limit')}
                {/if}
            </button>
        </div>
    </form>
</div>
